/**
 * Sistema de sonidos sintéticos usando Web Audio API.
 * No requiere archivos de audio externos.
 * El estado mute se persiste en localStorage.
 */

const SOUND_KEY = 'sounds_enabled';

export function isSoundEnabled(): boolean {
  try {
    const val = localStorage.getItem(SOUND_KEY);
    return val === null ? true : val === 'true'; // default: activado
  } catch {
    return true;
  }
}

export function setSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(SOUND_KEY, String(enabled));
  } catch {}
}

// Crear contexto de audio lazy
let ctx: AudioContext | null = null;
function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  try {
    if (!ctx) ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (ctx.state === 'suspended') ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

/**
 * Reproduce un tono sintético simple.
 */
function playTone(
  freq: number,
  duration: number,
  type: OscillatorType = 'sine',
  volume = 0.15,
  startDelay = 0,
  fadeOut = true
) {
  if (!isSoundEnabled()) return;
  const ac = getCtx();
  if (!ac) return;

  const oscillator = ac.createOscillator();
  const gainNode   = ac.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(ac.destination);

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(freq, ac.currentTime + startDelay);

  gainNode.gain.setValueAtTime(0.001, ac.currentTime + startDelay);
  gainNode.gain.linearRampToValueAtTime(volume, ac.currentTime + startDelay + 0.01);
  if (fadeOut) {
    gainNode.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + startDelay + duration);
  }

  oscillator.start(ac.currentTime + startDelay);
  oscillator.stop(ac.currentTime + startDelay + duration + 0.02);
}

/**
 * Campanilla sintética: oscilador principal + harmónico que se desafina levemente
 * para simular el "shimmer" de una campana real.
 */
function playCampanilla(freq: number, volume = 0.18, startDelay = 0) {
  if (!isSoundEnabled()) return;
  const ac = getCtx();
  if (!ac) return;

  const t = ac.currentTime + startDelay;

  // Oscilador principal — tono puro
  const osc1  = ac.createOscillator();
  const gain1 = ac.createGain();
  osc1.connect(gain1);
  gain1.connect(ac.destination);
  osc1.type = 'triangle';
  osc1.frequency.setValueAtTime(freq, t);
  gain1.gain.setValueAtTime(volume, t);
  gain1.gain.exponentialRampToValueAtTime(0.001, t + 0.9);
  osc1.start(t);
  osc1.stop(t + 0.95);

  // Harmónico levemente desafinado (×2.756) — crea el "ding"
  const osc2  = ac.createOscillator();
  const gain2 = ac.createGain();
  osc2.connect(gain2);
  gain2.connect(ac.destination);
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(freq * 2.756, t);
  gain2.gain.setValueAtTime(volume * 0.5, t);
  gain2.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
  osc2.start(t);
  osc2.stop(t + 0.55);

  // Sub-armónico suave para dar cuerpo
  const osc3  = ac.createOscillator();
  const gain3 = ac.createGain();
  osc3.connect(gain3);
  gain3.connect(ac.destination);
  osc3.type = 'sine';
  osc3.frequency.setValueAtTime(freq * 0.5, t);
  gain3.gain.setValueAtTime(volume * 0.15, t);
  gain3.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
  osc3.start(t);
  osc3.stop(t + 0.35);
}

// ── Sonidos específicos ───────────────────────────────────────────────────────

/**
 * Navegación entre páginas — dos clicks suaves y rápidos (igual al de antes,
 * pero ahora el login usa este mismo, como pediste).
 */
export function playNavigationSound() {
  playTone(650, 0.07, 'sine', 0.09);
  playTone(820, 0.09, 'sine', 0.08, 0.07);
}

/**
 * Login exitoso — logout al revés: tres notas ascendentes (C4→F4→A4),
 * como "encender" algo.
 */
export function playLoginSound() {
  playTone(261, 0.18, 'sine', 0.06);             // C4 — arranque suave
  playTone(349, 0.15, 'sine', 0.08, 0.20);       // F4
  playTone(440, 0.25, 'sine', 0.10, 0.38);       // A4 — remate
}

/**
 * Nuevo mensaje — campanilla triple: ding… ding… ding (más alto)
 * Muy distinto a los demás, reconocible como notificación de chat.
 */
export function playNewMessageSound() {
  playCampanilla(1046, 0.18, 0);       // C6 — primer ding
  playCampanilla(1046, 0.14, 0.32);    // C6 — segundo ding
  playCampanilla(1318, 0.16, 0.60);    // E6 — ding más alto al final
}

/**
 * Snackbar éxito — "acción realizada": dos notas ascendentes con bounce,
 * tipo confirmación de pago en app móvil.
 */
export function playSuccessSound() {
  playTone(523, 0.08, 'square', 0.06);           // C5 — nota inicial percusiva
  playTone(784, 0.18, 'sine',   0.12, 0.09);     // G5 — resolución
  playTone(1046, 0.25, 'sine',  0.10, 0.22);     // C6 — remate agudo
}

/**
 * Snackbar error — dos golpes graves descendentes, como "error" de sistema.
 */
export function playErrorSound() {
  playTone(220, 0.15, 'sawtooth', 0.08);         // A3 grave con textura
  playTone(174, 0.22, 'sine',     0.12, 0.18);   // F3 más grave, fade largo
}

/**
 * Snackbar info — campanada suave única, neutra.
 */
export function playInfoSound() {
  playCampanilla(740, 0.10, 0); // F#5 — una sola campanada discreta
}

/**
 * Logout — tono apagado descendente, como "apagar" algo.
 * Onda sine con filtro de paso bajo simulado via volumen bajo.
 */
export function playLogoutSound() {
  playTone(440, 0.12, 'sine', 0.10);             // A4
  playTone(349, 0.15, 'sine', 0.08, 0.13);       // F4
  playTone(261, 0.30, 'sine', 0.06, 0.27);       // C4 — apagado, fade largo
}
